/**
 * Currency Context and Provider for the NBRP Store
 */
'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// Supported currencies with exchange rates
export const SUPPORTED_CURRENCIES = {
  NOK: { name: 'Norwegian Krone', symbol: 'kr', rate: 1 },
  USD: { name: 'US Dollar', symbol: '$', rate: 0.094 },
  EUR: { name: 'Euro', symbol: '€', rate: 0.085 },
  GBP: { name: 'British Pound', symbol: '£', rate: 0.073 },
  SEK: { name: 'Swedish Krona', symbol: 'kr', rate: 1.05 },
  DKK: { name: 'Danish Krone', symbol: 'kr', rate: 0.63 },
} as const;

export type Currency = keyof typeof SUPPORTED_CURRENCIES;

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  convertPrice: (price: number) => number;
  formatPrice: (price: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

export function CurrencyProvider({ children }: CurrencyProviderProps) {
  const [currency, setCurrency] = useState<Currency>('NOK');
  const [mounted, setMounted] = useState(false);

  // Load saved currency from localStorage on mount
  useEffect(() => {
    const savedCurrency = localStorage.getItem('store-currency') as Currency;
    if (savedCurrency && savedCurrency in SUPPORTED_CURRENCIES) {
      setCurrency(savedCurrency);
    }
    setMounted(true);
  }, []);

  // Save currency to localStorage when it changes
  useEffect(() => {
    if (mounted) {
      localStorage.setItem('store-currency', currency);
    }
  }, [currency, mounted]);

  const convertPrice = (priceInNOK: number): number => {
    const rate = SUPPORTED_CURRENCIES[currency].rate;
    return priceInNOK * rate;
  };

  const formatPrice = (priceInNOK: number): string => {
    const convertedPrice = convertPrice(priceInNOK);
    
    // Get locale based on currency
    const locale = currency === 'NOK' ? 'nb-NO' : 
                  currency === 'USD' ? 'en-US' :
                  currency === 'EUR' ? 'de-DE' :
                  currency === 'GBP' ? 'en-GB' :
                  currency === 'SEK' ? 'sv-SE' :
                  currency === 'DKK' ? 'da-DK' : 'nb-NO';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: currency === 'NOK' || currency === 'SEK' || currency === 'DKK' ? 0 : 2,
      maximumFractionDigits: currency === 'NOK' || currency === 'SEK' || currency === 'DKK' ? 0 : 2,
    }).format(convertedPrice);
  };

  const value = {
    currency,
    setCurrency,
    convertPrice,
    formatPrice,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
